import { HttpClient } from "@angular/common/http";
import { Injectable, Injector } from "@angular/core";
import { OfficeSelectorService } from "../../../../common/officeSelector/officeSelector.service";
import "../../../../util/string";
import { KpiServiceError, KpiServiceErrorType } from "../../../../dashboardCommon/services/kpiServiceError";
import { ContractsKpiCfg, ContractsKpiCfgService } from "./contractsKpiCfg.service";
import { ContractsKpiService, Range } from "./contractsKpiService";
import { DateRange } from "./dateRange";
import { ImpersonationService } from "src/app/auth/apigee/impersonation.service";
import { ApigeeService } from "src/app/auth/apigee/apigee.service";

export class UnsignedContractsServiceData {
    public consultant: number;
    public office: number;
    public updateTimestamp: Date;
}

export abstract class UnsignedContractsService extends ContractsKpiService<UnsignedContractsServiceData> {
    protected abstract getRange(): DateRange;

    constructor(private serviceCfg: ContractsKpiCfgService, private apigeeService: ApigeeService, private impersonationService: ImpersonationService,
        private officeSelectorService: OfficeSelectorService, private httpClient: HttpClient, injector: Injector) {
        super(injector);
    }

    protected getDates(): Range {
        return this.getDateRange(this.getRange());
    }

    protected async update(): Promise<UnsignedContractsServiceData> {
        if (this.apigeeService.apiToken && this.officeSelectorService.selectedOffice) {
            let result: UnsignedContractsServiceData = null;
            try {
                let cfg: ContractsKpiCfg = this.serviceCfg.getCfg();
                let { begin, end } = this.getDateRange(this.getRange());
                let url = cfg.unsignedContracts.summaryEndpointPattern.format(this.officeSelectorService.selectedOffice.id, begin, end);
                result = await this.httpClient.post<UnsignedContractsServiceData>(url, this.impersonationService.getNifImpersonate()).toPromise();
            } catch (error) {
                let errorMessage = error.error ? error.error.message || error.error.errorMessage : error.message;
                throw new KpiServiceError(KpiServiceErrorType.requestError, "KpiServiceError-{0}-{1}".format(this.getClassName(), errorMessage));
            }

            if (result == null) throw new KpiServiceError(KpiServiceErrorType.noData, "KpiServiceError-{0}-No data".format(this.getClassName()));
            else return result;
        } else {
            throw new KpiServiceError(KpiServiceErrorType.missingInputParameters,"KpiServiceError-{0}-Missing input parameters".format(this.getClassName()));
        }
    }
}

@Injectable()
export class PastUnsignedContractsService extends UnsignedContractsService {
    public getClassName() { return "PastUnsignedContractsService"; }

    constructor(serviceCfg: ContractsKpiCfgService, apigeeService: ApigeeService, impersonationService: ImpersonationService,
        officeSelectorService: OfficeSelectorService, httpClient: HttpClient, injector: Injector) {
        super(serviceCfg, apigeeService, impersonationService, officeSelectorService, httpClient, injector);
    }

    protected getRange(): DateRange { return DateRange.Past; }
}

@Injectable()
export class InmediateUnsignedContractsService extends UnsignedContractsService {
    public getClassName() { return "InmediateUnsignedContractsService"; }

    constructor(serviceCfg: ContractsKpiCfgService, apigeeService: ApigeeService, impersonationService: ImpersonationService,
        officeSelectorService: OfficeSelectorService, httpClient: HttpClient, injector: Injector) {
        super(serviceCfg, apigeeService, impersonationService, officeSelectorService, httpClient, injector);
    }

    protected getRange(): DateRange { return DateRange.TodayTomorrow; }
}

@Injectable()
export class ForecastedUnsignedContractsService extends UnsignedContractsService {
    public getClassName() { return "ForecastedUnsignedContractsService"; }

    constructor(serviceCfg: ContractsKpiCfgService, apigeeService: ApigeeService, impersonationService: ImpersonationService,
        officeSelectorService: OfficeSelectorService, httpClient: HttpClient, injector: Injector) {
        super(serviceCfg, apigeeService, impersonationService, officeSelectorService, httpClient, injector);
    }

    protected getRange(): DateRange { return DateRange.Forecast; }
}

@Injectable()
export class TodayTomorrowUnsignedContractsService extends UnsignedContractsService {
    public getClassName() { return "TodayTomorrowUnsignedContractsService"; }

    constructor(serviceCfg: ContractsKpiCfgService, apigeeService: ApigeeService, impersonationService: ImpersonationService,
        officeSelectorService: OfficeSelectorService, httpClient: HttpClient, injector: Injector) {
        super(serviceCfg, apigeeService, impersonationService, officeSelectorService, httpClient, injector);
    }

    protected getRange(): DateRange { return DateRange.TodayTomorrow; }
}
